import { useMemo } from 'react';
import { useAppSelector } from '../../../common/hooks';
import { MarketPortfolios, Portfolio, TenantSupportedWeightType } from '../../../common/types';

export type MarketPortfolioTenure = 'Short Term' | 'Medium Term' | 'Long Term';

/**
 * Provides selected market portfolio sources for PortfolioComposition and AllocationsOverTime, following business rules:
 * - If both tactical and strategic exist: PortfolioComposition uses tactical; AllocationsOverTime uses strategic.
 * - If only one exists: Use the available one for both.
 * - If tenant is configured as `strategic_only` but strategic data is missing, fall back to tactical.
 */

// Add optional param for short, med, long terms and bundle type
export const useMarketPortfolioData = (tenure?: MarketPortfolioTenure) => {
    const marketPortfolios: MarketPortfolios = useAppSelector((state) => state.global.marketPortfolios) as any;
    // AEX-3108: only multiportfolio tenants (Banorte) hold term-scoped portfolios; others use the single default set.
    const isMultiPortfolioTenant = useAppSelector((state) => Boolean(state?.global?.globalConfig?.isMultiPortfolioTenant));
    const adviceWeightTypes: TenantSupportedWeightType =
        useAppSelector((state) => state?.global?.globalConfig?.tenant_supported_weight_type) || 'both'; // giving a fallback of 'both' just in case
    const banorteWeightTypes =
        (tenure &&
            useAppSelector(
                (state) => state?.global?.globalConfig?.goalCategoryPortfolioMappings?.[tenure]?.supportedWeightType
            )) ||
        'both';
    const weightTypes = isMultiPortfolioTenant ? banorteWeightTypes : adviceWeightTypes;
    const termScopedMarketPortfolios = useMemo(() => {
        if (!isMultiPortfolioTenant || !tenure) return null;

        const termKeyMap: Record<'Short Term' | 'Medium Term' | 'Long Term', 'shortTerm' | 'mediumTerm' | 'longTerm'> =
            {
                'Short Term': 'shortTerm',
                'Medium Term': 'mediumTerm',
                'Long Term': 'longTerm'
            };

        const scoped = marketPortfolios?.[termKeyMap[tenure]];
        if (!scoped) return null;

        return scoped;
    }, [isMultiPortfolioTenant, tenure, marketPortfolios]);

    // Extract portfolios and memoize to avoid recreating arrays on every render
    const tactical: Portfolio[] = useMemo(
        () => termScopedMarketPortfolios?.tactical || marketPortfolios?.tactical || [],
        [termScopedMarketPortfolios, marketPortfolios]
    );
    const strategic: Portfolio[] = useMemo(
        () => termScopedMarketPortfolios?.strategic || marketPortfolios?.strategic || [],
        [termScopedMarketPortfolios, marketPortfolios]
    );

    // Decide sources based on tenant configuration and availability
    const { portfolioCompositionSource, allocationOverTimeSource } = useMemo(() => {
        // Helper fallbacks
        const hasTactical = Array.isArray(tactical) && tactical.length > 0;
        const hasStrategic = Array.isArray(strategic) && strategic.length > 0;

        // If tenant is configured to use strategic only
        if (weightTypes === 'strategic_only') {
            if (hasStrategic) {
                return { portfolioCompositionSource: strategic, allocationOverTimeSource: strategic };
            }
            // strategic expected but missing -> fall back to tactical if available
            if (hasTactical) {
                return { portfolioCompositionSource: tactical, allocationOverTimeSource: tactical };
            }
            return { portfolioCompositionSource: [], allocationOverTimeSource: [] };
        }

        // weightTypes === 'both'
        if (hasTactical && hasStrategic) {
            // Both uploaded: composition = tactical, allocations/projections = strategic
            return { portfolioCompositionSource: tactical, allocationOverTimeSource: strategic };
        }

        // Only strategic uploaded
        if (hasStrategic) {
            return { portfolioCompositionSource: strategic, allocationOverTimeSource: strategic };
        }

        // Only tactical uploaded
        if (hasTactical) {
            return { portfolioCompositionSource: tactical, allocationOverTimeSource: tactical };
        }

        // default empty
        return { portfolioCompositionSource: [], allocationOverTimeSource: [] };
    }, [weightTypes, tactical, strategic]);

    // Expose memoized values
    return {
        tactical,
        strategic,
        portfolioCompositionSource,
        allocationOverTimeSource
    };
};

export default useMarketPortfolioData;
